import { useState } from 'react';
import { api, type UploadKind } from '../api/client';
import { saveBlob } from '../lib/saveBlob';
import { Button } from './ui';

// The file a reading or commission arrived in (a sheet or a photo), fetched
// with the user's token and saved under the name it was sent with. The server
// decides who may download it (billing-api uploads/access.ts).

const ERRORS: Record<string, string> = {
  no_original: 'No original file is stored for this row.',
  original_missing: 'The original file is no longer in storage.',
  'Not found': 'You cannot download this original.',
};

export function DownloadOriginalButton({
  kind,
  id,
  fallbackName,
  size = 'sm',
  label = 'Download original',
}: {
  kind: UploadKind;
  id: string;
  fallbackName?: string;
  size?: 'sm' | 'md';
  /** Shorter where a column header already says "Original" (the Uploads table). */
  label?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    setBusy(true);
    setError(null);
    try {
      const { blob, filename } = await api.downloadOriginal(kind, id, fallbackName);
      saveBlob(blob, filename);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Download failed';
      setError(ERRORS[message] ?? message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <Button variant="secondary" size={size} onClick={download} disabled={busy}>
        {busy ? 'Downloading…' : label}
      </Button>
      {error && (
        <span role="alert" className="text-xs text-red-700 dark:text-red-400">
          {error}
        </span>
      )}
    </span>
  );
}
